import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { CoverCard } from '@shared/ui/CoverCard'
import { ROUTES } from '@shared/config/routes'
import { useCards, useCourses } from '../../../hooks/useApi'
import FavoriteButton from './FavoriteButton'

interface SearchResultsProps {
  query: string
}

/**
 * Результаты поиска на главной: карточки и курсы в одном списке.
 *
 * Свой список, а не сетка из «Базы»: там карточки остаются в прежнем виде, а
 * на главной по макету другие — вытянутые, с затемнением.
 */
const SearchResults = ({ query }: SearchResultsProps) => {
  const navigate = useNavigate()
  const { data: cardsData } = useCards()
  const { data: coursesData } = useCourses()

  const needle = query.trim().toLowerCase()

  const cards = useMemo(
    () =>
      (cardsData?.cards ?? []).filter(card =>
        card.title.toLowerCase().includes(needle)
      ),
    [cardsData, needle]
  )

  const courses = useMemo(
    () =>
      (coursesData?.courses ?? []).filter(course =>
        course.base.name.toLowerCase().includes(needle)
      ),
    [coursesData, needle]
  )

  const total = cards.length + courses.length

  if (total === 0) {
    return (
      <div className="home-search-empty">
        <p>По запросу «{query}» ничего не нашлось</p>
        <p>Попробуйте другое слово</p>
      </div>
    )
  }

  return (
    <section className="home-search">
      <p className="home-search__count">Нашлось: {total}</p>
      <div className="home-search__grid">
        {courses.map(course => (
          <CoverCard
            key={`course-${course.base.course_id}`}
            title={course.base.name}
            caption="Курс"
            image={course.base.preview_icon_url}
            isLocked={!course.access}
            onClick={() =>
              navigate(ROUTES.COURSE_DETAIL(course.base.course_id))
            }
          />
        ))}
        {cards.map(card => (
          <CoverCard
            key={card.card_id}
            title={card.title}
            caption={card.categories?.[0]?.name}
            image={card.icon_url}
            action={<FavoriteButton cardId={card.card_id} />}
            onClick={() => navigate(ROUTES.CARD(card.card_id))}
          />
        ))}
      </div>
    </section>
  )
}

export default SearchResults
